/**
 * DataRowInspector - Right panel inspector for data row blocks
 * 
 * Shows configuration fields specific to each data row type:
 * - series_carousel, novel_carousel, artwork_gallery
 * - continue_reading, trending_series, new_releases, featured_creators
 */
import React from 'react';
import {
  Layers, TrendingUp, BookOpen, Palette, Clock, Star, Settings
} from 'lucide-react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Slider } from '../ui/slider';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { DATA_BLOCK_TYPES } from './AddSectionPanel';

const BLOCK_ICONS = {
  series_carousel: BookOpen,
  novel_carousel: BookOpen,
  artwork_gallery: Palette,
  continue_reading: Clock,
  trending_series: TrendingUp,
  new_releases: Star,
  featured_creators: Star,
};

const DataRowInspector = ({ section, onUpdateProps }) => {
  if (!section) return null;

  const blockDef = DATA_BLOCK_TYPES[section.type];
  const props = section.props || {};
  const defaults = blockDef?.defaultProps || {};
  const Icon = BLOCK_ICONS[section.type] || Layers;

  const handleChange = (key, value) => {
    onUpdateProps(key, value);
  };

  return (
    <aside
      className="w-[260px] bg-zinc-950 border-l border-zinc-800/30 flex flex-col h-full"
      data-testid="data-row-inspector"
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-zinc-800/30">
        <h2 className="text-[11px] font-medium text-zinc-400 tracking-wide">CONFIGURE</h2>
      </div>

      {/* Block identifier */}
      <div className="px-4 py-3 border-b border-zinc-800/30 bg-zinc-900/30">
        <div className="flex items-center gap-3">
          <div className="p-1.5 bg-orange-500/20 rounded">
            <Icon className="w-4 h-4 text-orange-400" />
          </div>
          <div>
            <div className="text-[13px] font-medium text-white">{blockDef?.name || section.type}</div>
            <div className="text-[11px] text-zinc-500">{blockDef?.description || 'Data row'}</div>
          </div>
        </div>
      </div>

      {/* Config fields */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Title */}
        <div className="space-y-1.5">
          <Label className="text-zinc-400 text-[11px]">Row Title</Label>
          <Input
            value={props.title || ''}
            onChange={(e) => handleChange('title', e.target.value)}
            placeholder="Section title..."
            className="bg-zinc-900 border-zinc-800 text-white h-8 text-[12px]"
            data-testid="data-row-title-input"
          />
        </div>

        {/* Source */}
        {'source' in defaults && (
          <div className="space-y-1.5">
            <Label className="text-zinc-400 text-[11px]">Data Source</Label>
            <Select
              value={props.source || 'featured'}
              onValueChange={(v) => handleChange('source', v)}
            >
              <SelectTrigger
                className="bg-zinc-900 border-zinc-800 text-white h-8 text-[12px]"
                data-testid="data-row-source-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                <SelectItem value="featured">Featured</SelectItem>
                <SelectItem value="trending">Trending</SelectItem>
                <SelectItem value="new">New Releases</SelectItem>
                <SelectItem value="following">Following</SelectItem>
                <SelectItem value="tag">By Tag</SelectItem>
                <SelectItem value="curated">Curated</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Tag filter (only when source = tag) */}
        {props.source === 'tag' && (
          <div className="space-y-1.5">
            <Label className="text-zinc-400 text-[11px]">Tag</Label>
            <Input
              value={props.tag || ''}
              onChange={(e) => handleChange('tag', e.target.value)}
              placeholder="e.g., action, romance"
              className="bg-zinc-900 border-zinc-800 text-white h-8 text-[12px]"
            />
          </div>
        )}

        {/* Max Items */}
        <div className="space-y-1.5">
          <div className="flex justify-between">
            <Label className="text-zinc-400 text-[11px]">Max Items</Label>
            <span className="text-[11px] text-zinc-500">{props.limit || 10}</span>
          </div>
          <Slider
            value={[props.limit || 10]}
            onValueChange={([v]) => handleChange('limit', v)}
            min={3}
            max={20}
            step={1}
            className="py-1"
          />
        </div>

        {/* Card Style */}
        {'cardVariant' in defaults && (
          <div className="space-y-1.5">
            <Label className="text-zinc-400 text-[11px]">Card Style</Label>
            <Select
              value={props.cardVariant || 'discovery'}
              onValueChange={(v) => handleChange('cardVariant', v)}
            >
              <SelectTrigger className="bg-zinc-900 border-zinc-800 text-white h-8 text-[12px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                <SelectItem value="discovery">Discovery</SelectItem>
                <SelectItem value="reading">Reading</SelectItem>
                <SelectItem value="featured">Featured</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Layout (artwork gallery) */}
        {'layout' in defaults && (
          <div className="space-y-1.5">
            <Label className="text-zinc-400 text-[11px]">Layout</Label>
            <Select
              value={props.layout || 'carousel'}
              onValueChange={(v) => handleChange('layout', v)}
            >
              <SelectTrigger className="bg-zinc-900 border-zinc-800 text-white h-8 text-[12px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                <SelectItem value="carousel">Carousel</SelectItem>
                <SelectItem value="grid">Grid</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Grid columns (when layout=grid) */}
        {props.layout === 'grid' && (
          <div className="space-y-1.5">
            <div className="flex justify-between">
              <Label className="text-zinc-400 text-[11px]">Grid Columns</Label>
              <span className="text-[11px] text-zinc-500">{props.columns || 4}</span>
            </div>
            <Slider
              value={[props.columns || 4]}
              onValueChange={([v]) => handleChange('columns', v)}
              min={2}
              max={6}
              step={1}
              className="py-1"
            />
          </div>
        )}

        {/* See All Link */}
        {'seeAllLink' in defaults && (
          <div className="space-y-1.5">
            <Label className="text-zinc-400 text-[11px]">See All Link</Label>
            <Input
              value={props.seeAllLink || ''}
              onChange={(e) => handleChange('seeAllLink', e.target.value)}
              placeholder="/browse"
              className="bg-zinc-900 border-zinc-800 text-white h-8 text-[12px]"
            />
          </div>
        )}

        {/* See All Text */}
        {'seeAllText' in defaults && (
          <div className="space-y-1.5">
            <Label className="text-zinc-400 text-[11px]">See All Text</Label>
            <Input
              value={props.seeAllText || 'See All'}
              onChange={(e) => handleChange('seeAllText', e.target.value)}
              className="bg-zinc-900 border-zinc-800 text-white h-8 text-[12px]"
            />
          </div>
        )}

        {/* Show Rank */}
        {'showRank' in defaults && (
          <div className="flex items-center justify-between">
            <Label className="text-zinc-400 text-[11px]">Show Rank Numbers</Label>
            <Switch
              checked={props.showRank || false}
              onCheckedChange={(v) => handleChange('showRank', v)}
            />
          </div>
        )}

        {/* Show Progress (continue_reading) */}
        {'showProgress' in defaults && (
          <div className="flex items-center justify-between">
            <Label className="text-zinc-400 text-[11px]">Show Progress Bar</Label>
            <Switch
              checked={props.showProgress !== false}
              onCheckedChange={(v) => handleChange('showProgress', v)}
            />
          </div>
        )}

        {/* Enabled toggle */}
        <div className="flex items-center justify-between pt-2 border-t border-zinc-800/30">
          <Label className="text-zinc-400 text-[11px]">Enabled</Label>
          <Switch
            checked={section.enabled !== false}
            onCheckedChange={(v) => handleChange('__enabled', v)}
          />
        </div>
      </div>

      {/* Footer - Block ID */}
      <div className="p-3 border-t border-zinc-800/30">
        <div className="space-y-1">
          <Label className="text-zinc-500 text-[10px]">Block ID</Label>
          <p className="text-[10px] font-mono text-zinc-600 truncate">{section.id}</p>
        </div>
      </div>
    </aside>
  );
};

export default DataRowInspector;
